/**
 * 地磅客户端 — 调用 jinglunGo 本地外设服务（http://127.0.0.1:8765）的地磅能力。
 *
 * 替代旧的 weighbridgeReader.js（浏览器 Web Serial）+ webbox serial-manager（Node serialport）。
 * 串口打开/读取/耀华协议解析/波特率探测/watchdog 全部在 jinglunGo 后端完成，
 * 前端只通过 HTTP（配置/控制）+ WebSocket（实时读数推送）交互。
 *
 * 设计模式照 jinglunReader.js（同走 8765 的 NFC 读卡器客户端）保持一致：
 *   - axios 实例 + 10s 超时
 *   - request() 统一拆包 {ok,data}，失败 throw 归一化错误
 *   - normalizeError 错误码体系（SERVICE_UNAVAILABLE / SCALE_OPEN_FAILED / ...）
 *   - serviceLost 标志 + 自愈（8765 不可达时不放弃，定时探活）
 *
 * WebSocket：
 *   - ws://127.0.0.1:8765/api/scale/stream（loopback 在 HTTPS 页面属混合内容豁免，不需 wss）
 *   - jinglunGo 推 {type, scaleKey, weight, stable, connected} 消息
 *   - 带自动重连（断线后定时重连，参考 jinglunReader 的 serviceLost 自愈）
 */

import axios from 'axios'
// [2026-09-09] "服务未启动"和"浏览器拦了本地网络权限"在前端都是 Network Error, 提示两者都覆盖
import { peripheralAccessHint } from './peripheralHealth'

const BASE_URL = 'http://127.0.0.1:8765'
// WebSocket URL：loopback ws:// 在 HTTPS 页面属混合内容豁免，不需 wss
const WS_URL = 'ws://127.0.0.1:8765/api/scale/stream'

const client = axios.create({
  baseURL: BASE_URL,
  timeout: 10000
})

// ===== 错误归一化（照 jinglunReader.js 模式）=====

/**
 * 判断错误是否为"外设服务不可达"（网络错误 / SERVICE_UNAVAILABLE）。
 * 这类错误不应让上层放弃 — jinglunGo 重启后能自愈。
 */
export function isServiceUnavailableError(error) {
  if (!error) return false
  if (error.code === 'SERVICE_UNAVAILABLE') return true
  const msg = error.message || ''
  return msg.indexOf('Network Error') >= 0 || msg.indexOf('ECONN') >= 0
}

function normalizeError(error) {
  // 后端返回的业务错误（{ok:false,error:{code,message}}）
  if (error && error.response && error.response.data && error.response.data.error) {
    const err = error.response.data.error
    return {
      code: err.code || 'SCALE_ERROR',
      message: err.message || '地磅服务返回错误'
    }
  }
  // 透传已归一化的错误
  if (error && error.error) return error.error
  // 网络错误 → SERVICE_UNAVAILABLE
  if (error && error.message) {
    if (error.message.indexOf('Network Error') >= 0 || error.message.indexOf('Failed to fetch') >= 0) {
      return {
        code: 'SERVICE_UNAVAILABLE',
        message: '外设服务不可达。' + peripheralAccessHint()
      }
    }
    return { code: 'REQUEST_FAILED', message: error.message }
  }
  return { code: 'UNKNOWN_ERROR', message: '地磅服务调用失败' }
}

/**
 * 统一请求封装：成功返回 data，失败 throw 归一化错误 {code, message}。
 * 与 jinglunReader.js 的 request() 完全同构。
 * @param {number} [timeout] 单次超时覆盖(ms)，不传用 client 默认 10s（ONVIF 扫描/多轮 SOAP 较慢需加长）
 */
async function request(method, url, data, timeout) {
  try {
    const res = await client.request({ method, url, data, ...(timeout ? { timeout } : {}) })
    if (!res.data || res.data.ok !== true) {
      throw res.data || { error: { message: '地磅服务返回异常' } }
    }
    return res.data.data
  } catch (error) {
    throw normalizeError(error)
  }
}

// ===== HTTP API =====

const ScaleClient = {
  baseURL: BASE_URL,
  wsUrl: WS_URL,

  /** 健康检查（复用全局 /api/health，确认 8765 在线） */
  async health() {
    return request('get', '/api/health', null)
  },

  /** 枚举可用 COM 口（替代 webbox serial:list） */
  async listPorts() {
    const data = await request('get', '/api/scale/ports', null)
    return (data && data.ports) || []
  },

  /**
   * 打开串口（替代 webbox serial:open + Web Serial connect）
   * @param {Object} opts { scaleKey, port, baudRate, protocol, force }
   *   force=true 绕过服务端"同参数已连接"幂等短路, 强制关重开（新鲜度看门狗自愈用）
   * @returns {Promise<Object>} { scaleKey, port, baudRate, connected, weight, stable }
   */
  async openScale(opts = {}) {
    return request('post', '/api/scale/open', {
      scaleKey: opts.scaleKey || '',
      port: opts.port || '',
      baudRate: Number(opts.baudRate) || 9600,
      protocol: opts.protocol || 'yaohua-continuous',
      force: !!opts.force
    })
  },

  /** 关闭串口（替代 webbox serial:close） */
  async closeScale(scaleKey) {
    return request('post', '/api/scale/close', { scaleKey })
  },

  // [2026-09-04] 已删除的前端死封装: probeBaud / startPortProbe / getPortProbeStatus /
  //   readOnce / getConfig —— 三处检测按钮合并为统一端口诊断(startDiagnose)后无调用方;
  //   对应 Go 端点保留(jinglunGo 内置网页的「自动探测」等仍在用)。

  /** 双磅状态快照 */
  async getStatus() {
    return request('get', '/api/scale/status', null)
  },


  /**
   * [2026-09-04] 数据门禁：等待某磅收到有效读数（串口 open 成功 ≠ 连接成功）。
   *
   * 背景：串口选错/波特率选错时 OS 层 open 恒成功，若 open 即亮"已连接"，
   * 用户会看到一瞬间的"已连接 + 0kg"再断开，非常误导。连接成功的判据必须是
   * "收到并解析出有效重量帧"。
   *
   * 双通道判定（先到先赢）：
   *   ① WS reading 推送：startedAt 之后到达的读数（lastReadingAt 客户端时间戳）
   *   ② /api/scale/status 快照的 lastFrameAt：服务端最近一次协议解析成功帧
   *      （毫秒时间戳，与本机同钟 — jinglunGo 与浏览器同机运行，可直接比较；WS 断链时兜底）
   *
   * @param {string} scaleKey 'entry' | 'exit'（也兼容 'entryScale'/'exitScale'）
   * @param {number} [timeoutMS=5000] 等待窗口（须短于服务端 6s 数据看门狗，抢在自愈前判定）
   * @param {number} [startedAt] 起始时刻（默认当前；调用方在 openScale 之前取好传入更准）
   * @returns {Promise<boolean>} true=窗口内等到了有效读数
   */
  async waitForReading(scaleKey, timeoutMS = 5000, startedAt = Date.now()) {
    const key = scaleKey === 'entryScale' ? 'entry' : (scaleKey === 'exitScale' ? 'exit' : scaleKey)
    const beginAt = startedAt
    while (Date.now() - beginAt < timeoutMS) {
      const lastAt = ScaleStream.lastReadingAt(key)
      if (lastAt && lastAt >= beginAt) return true
      try {
        const st = await this.getStatus()
        const one = st && st[key]
        if (one && one.lastFrameAt && one.lastFrameAt >= beginAt) return true
      } catch (e) { /* 快照失败不致命，继续等 WS */ }
      await new Promise((r) => setTimeout(r, 200))
    }
    return false
  },


  /** 写地磅配置（持久化到 jinglunGo settings.json） */
  async saveConfig(scales) {
    return request('post', '/api/scale/config', { scales })
  },

  /**
   * [2026-08-17] 检测串口被哪个进程占用（服务端全系统句柄表枚举）。
   * @param {string} port 'COM3'
   * @returns {Promise<Object>} {port, exists, inUse, selfHolding, holders:[{pid,name,image,hint}], suspects?, note?}
   */
  async checkPortHolder(port) {
    return request('get', '/api/scale/port-holder?port=' + encodeURIComponent(port || ''), null)
  },



  /**
   * [2026-08-27] 启动协议检测任务（异步：逐档波特率抓原始字节 → 离线跑全部协议解析器，耀华优先）。
   * 进度用 getProtocolDetectStatus 轮询。返回任务快照 {taskId, port, bauds, tried, running}。
   */
  async startProtocolDetect(opts = {}) {
    return request('post', '/api/scale/detect-protocol', {
      port: opts.port || '',
      candidateBauds: Array.isArray(opts.candidateBauds) ? opts.candidateBauds : undefined
    })
  },

  /**
   * [2026-08-27] 查询协议检测进度。
   * @returns {Promise<Object>} {running, port, currentBaud, tried:[{baud,status,bytes,rawHex,note}],
   *   hitBaud, hitProtocol, hitProtocolLabel, weight, hint?, error?}
   */
  async getProtocolDetectStatus() {
    return request('get', '/api/scale/detect-protocol/status', null)
  },

  /**
   * [2026-08-17] 读某工厂的过磅设备配置（好粮宝本地服务持久真身）。
   * @param {string} factory 工厂 ID 或 '_global'（本机全局配置）
   * @returns {Promise<Object>} {factory, entries: {key: {v, t}}}
   */
  async getWeighConfig(factory) {
    return request('get', '/api/weigh-config?factory=' + encodeURIComponent(factory || ''), null)
  },

  /**
   * [2026-08-17] 合并写过磅设备配置（服务端逐 key 时间戳新者胜）。
   * @returns {Promise<Object>} 合并后的全量 {factory, entries}
   */
  async saveWeighConfig(factory, entries) {
    return request('post', '/api/weigh-config', { factory, entries })
  },

  /**
   * [2026-09-04] 统一端口诊断（异步任务）：存在性→占用→驱动硬件→协议+波特率，一次跑完。
   * 进度用 getDiagnoseStatus 轮询。返回任务快照 {taskId, port, running, steps, tried}。
   */
  async startDiagnose(opts = {}) {
    return request('post', '/api/scale/diagnose', {
      port: opts.port || '',
      candidateBauds: Array.isArray(opts.candidateBauds) ? opts.candidateBauds : undefined
    })
  },

  /**
   * [2026-09-04] 查询统一端口诊断进度。
   * @returns {Promise<Object>} {running, steps:[{key,title,status,summary,detail,solution,data}], tried, hitBaud?, hitProtocol?...}
   */
  async getDiagnoseStatus() {
    return request('get', '/api/scale/diagnose/status', null)
  },

  /**
   * [2026-09-04] 读系统信息（OS 版本识别 + UPort 驱动官方建议）。
   * @returns {Promise<Object>} {name, display, build, release, isWin7/isWin8x/isWin10/isWin11, uportRecommended, uportAdvice}
   */
  async getSystemInfo() {
    return request('get', '/api/system/info', null)
  },

  /**
   * [2026-09-04] 一键重装 UPort 驱动（卸载旧版任意版本 → 安装随包 v3.1）。
   * 服务端同步执行，卸载+安装约 10~60 秒，默认 240s HTTP 超时。
   * @returns {Promise<Object>} {os, steps:[{key,title,status,summary,detail}], success, needReboot, recommended, advice, finalAdvice}
   */
  async reinstallUPort() {
    return request('post', '/api/drivers/reinstall-uport', {}, 240000)
  },

  /**
   * [2026-09-04] 重启外设服务（拉起接力子进程后优雅退出，约 2~5 秒接管 8765）。
   * 托盘卡死时用这个比 shutdown 可靠（不依赖 webbox 看门狗拉活）。
   */
  async restartService() {
    return request('post', '/api/service/restart', {}, 15000)
  },

  // ===== [无中心模式] ONVIF 摄像头自动发现 =====

  /**
   * 扫描局域网 ONVIF 设备（WS-Discovery UDP 多播，约需 5~9s）。
   * @returns {Promise<{devices: [{address, xaddr, types, scopes}], count, note}>}
   *   注意：防火墙拦截多播时 devices 为空数组，不报错。
   */
  async onvifDiscover() {
    return request('get', '/api/onvif/discover', null, 20000)
  },

  /**
   * 对指定 ONVIF 设备取真实 RTSP 取流地址（GetStreamUri，多 Profile 多轮 SOAP）。
   * @param {Object} opts { xaddr, username, password }  xaddr 为 discover 返回的设备服务地址
   * @returns {Promise<{streams: [{profileToken, uri, ...}], count, xaddr}>}
   */
  async onvifStreamUris(opts = {}) {
    return request('post', '/api/onvif/stream-uris', {
      xaddr: opts.xaddr || '',
      username: opts.username || '',
      password: opts.password || ''
    }, 20000)
  },

  /**
   * 预检：8765 在不在线 + 地磅能力是否就绪。
   * 返回 { ok, code?, message? }，不抛异常（照 jinglunReader.ensureDeviceReady 模式）。
   */
  async ensureReady() {
    try {
      await this.health()
      return { ok: true }
    } catch (e) {
      return {
        ok: false,
        code: 'SERVICE_UNAVAILABLE',
        message: peripheralAccessHint()
      }
    }
  }
}

// ===== WebSocket 实时推送订阅 =====
//
// 全局单例 WS 连接（多磅共享一条连接，消息靠 scaleKey 区分）。
// 带自动重连：断线后定时重连，重连成功后 jinglunGo 会立即推一次状态快照恢复订阅。

let ws = null
let wsConnected = false
let wsReconnectTimer = null
let wsManualClose = false // 是否主动关闭（true 时不自动重连）
let wsReconnectDelay = 1000 // 重连退避（1s 起，最多 15s）

// 订阅者回调列表（支持多组件订阅同一 WS）
const readingCallbacks = [] // (msg) => void  msg={scaleKey, weight, stable}
const statusCallbacks = [] // (msg) => void  msg={scaleKey, connected, ...}
const errorCallbacks = [] // (msg) => void  msg={scaleKey, code, message}
const wsOpenCallbacks = [] // () => void  WS (重)连成功（mixin 用来做服务端状态对账）

// [2026-08-24] 每个磅最近一次收到 reading 推送的时刻（数据新鲜度看门狗用）
const lastReadingAtByScale = {}

function notify(list, payload) {
  for (let i = list.length - 1; i >= 0; i--) {
    try { list[i](payload) } catch (e) { /* 单个回调异常不影响其它 */ }
  }
}

function scheduleReconnect() {
  if (wsManualClose) return
  if (wsReconnectTimer) return
  wsReconnectDelay = Math.min(wsReconnectDelay * 1.5, 15000) // 退避，上限 15s
  wsReconnectTimer = setTimeout(() => {
    wsReconnectTimer = null
    openWSInternal()
  }, wsReconnectDelay)
}

function openWSInternal() {
  try {
    ws = new WebSocket(WS_URL)
  } catch (e) {
    scheduleReconnect()
    return
  }

  ws.onopen = () => {
    wsConnected = true
    wsReconnectDelay = 1000 // 重置退避
    // [2026-08-17] 通知订阅者：WS (重)连成功。服务端会推一次状态快照，
    // 但订阅方还应主动对账（快照可能因断线期间的连接变化而错过）。
    notify(wsOpenCallbacks, {})
  }

  ws.onmessage = (event) => {
    let msg
    try { msg = JSON.parse(event.data) } catch (e) { return }
    if (!msg || !msg.type) return
    if (msg.type === 'reading') {
      lastReadingAtByScale[msg.scaleKey || ''] = Date.now()
      notify(readingCallbacks, msg)
    } else if (msg.type === 'status') {
      notify(statusCallbacks, msg)
    } else if (msg.type === 'error') {
      notify(errorCallbacks, msg)
    }
  }

  ws.onclose = () => {
    wsConnected = false
    ws = null
    if (!wsManualClose) scheduleReconnect()
  }

  ws.onerror = () => {
    // onclose 会跟进处理重连，这里不重复
  }
}

const ScaleStream = {
  /** WS 是否已连接 */
  isConnected() { return wsConnected },

  /**
   * [2026-08-24] 某磅最近一次收到 reading 推送的时刻（从未收到返回 0）。
   * 数据新鲜度看门狗用: connected=true 但长时间无 reading → 链路僵死, 需自愈。
   */
  lastReadingAt(scaleKey) { return lastReadingAtByScale[scaleKey] || 0 },

  /**
   * [2026-08-24] 强制重建 WS 连接（看门狗判定推送通道僵死时调用）。
   * 与 close() 不同: 不停止自动重连, 而是立即重开。先把旧连接的 onclose 摘掉,
   * 避免旧连接的关闭事件再触发一次 scheduleReconnect 造成双 WS。
   */
  forceReconnect() {
    wsManualClose = false
    if (wsReconnectTimer) { clearTimeout(wsReconnectTimer); wsReconnectTimer = null }
    if (ws) {
      try { ws.onclose = null; ws.onerror = null; ws.close() } catch (e) {}
      ws = null
    }
    wsConnected = false
    openWSInternal()
  },

  /**
   * 订阅地磅读数推送。
   * @param {Function} onReading (msg) => void，msg={scaleKey, weight, stable}
   * @param {Function} [onStatus] (msg) => void，msg={scaleKey, connected, ...}
   * @param {Function} [onError]  (msg) => void，msg={scaleKey, code, message}
   * @param {Function} [onOpen]   () => void，WS (重)连成功（做服务端状态对账用）
   * @returns {Function} 取消订阅函数
   */
  subscribe(onReading, onStatus, onError, onOpen) {
    if (typeof onReading === 'function') readingCallbacks.push(onReading)
    if (typeof onStatus === 'function') statusCallbacks.push(onStatus)
    if (typeof onError === 'function') errorCallbacks.push(onError)
    if (typeof onOpen === 'function') wsOpenCallbacks.push(onOpen)
    // 首个订阅者触发 WS 连接
    if (!ws && !wsManualClose) openWSInternal()
    return () => {
      const ri = readingCallbacks.indexOf(onReading)
      if (ri >= 0) readingCallbacks.splice(ri, 1)
      const si = statusCallbacks.indexOf(onStatus)
      if (si >= 0) statusCallbacks.splice(si, 1)
      const ei = errorCallbacks.indexOf(onError)
      if (ei >= 0) errorCallbacks.splice(ei, 1)
      const oi = wsOpenCallbacks.indexOf(onOpen)
      if (oi >= 0) wsOpenCallbacks.splice(oi, 1)
    }
  },

  /** 关闭 WS（组件卸毁时调用；会停止自动重连） */
  close() {
    wsManualClose = true
    if (wsReconnectTimer) { clearTimeout(wsReconnectTimer); wsReconnectTimer = null }
    if (ws) { try { ws.close() } catch (e) {} ws = null }
    wsConnected = false
  },

  /** 重新允许自动重连（组件重新 mounted 时调 close 后再重新订阅会自动重开） */
  reset() {
    wsManualClose = false
    if (!ws) openWSInternal()
  }
}

// ===== [2026-09-04] 通用智能连接（全项目唯一的地磅连接编排入口） =====
//
// 顶栏「检测连接」/ 地磅测试页「一键连接」都走这一个函数，别处不要再自己编排
// openScale / diagnose 的连接组合。
//
// 流程（jinglunGo 模式）：
//   ① 按传入参数（通常来自设备配置=之前成功过的组合）直连 + waitForReading 数据门禁
//   ② 失败 → 关口，跑统一端口诊断（存在性→占用→驱动→协议+波特率逐档识别）
//   ③ 诊断命中 → 用命中参数重连 + 数据门禁；仍失败 → 关口
//   ④ 全失败 → 返回第一个 fail 步骤的 summary/solution 作为失败原因
//
// @param {Object} opts { scaleKey:'entry'|'exit', port, baudRate, protocol,
//   onStage?: (stage, info) => void }
//   stage: 'config-connect' | 'diagnose' | 'diagnose-hit' | 'done'
// @returns {Promise<Object>} { ok, via:'config'|'diagnose', port, baudRate, protocol,
//   failStep?, failSummary?, failSolution?, error? }
async function smartConnectScale(opts) {
  const key = opts.scaleKey === 'entryScale' ? 'entry' : (opts.scaleKey === 'exitScale' ? 'exit' : opts.scaleKey)
  const port = opts.port
  const protocol = opts.protocol || 'yaohua-continuous'
  const cfgBaud = Number(opts.baudRate) || 9600
  const onStage = typeof opts.onStage === 'function' ? opts.onStage : () => {}

  // ① 按已有参数直连（含数据门禁：串口 open 成功 ≠ 连接成功）
  onStage('config-connect', { port, baudRate: cfgBaud })
  try {
    const startedAt = Date.now()
    await ScaleClient.openScale({ scaleKey: key, port, baudRate: cfgBaud, protocol })
    if (await ScaleClient.waitForReading(key, 5000, startedAt)) {
      onStage('done', { ok: true, via: 'config' })
      return { ok: true, via: 'config', port, baudRate: cfgBaud, protocol }
    }
    // open 成功但收不到数 → 关口（desired=false 止住服务端空转）再走诊断
    try { await ScaleClient.closeScale(key) } catch (e) { /* 忽略 */ }
  } catch (e) { /* open 失败（被占/不存在）交给诊断给出根因 */ }

  // ② 统一端口诊断（服务端全局单任务；onStage 在任务启动成功后才发，页面据此开面板轮询）
  await ScaleClient.startDiagnose({ port })
  onStage('diagnose', { port })
  const st = await pollDiagnoseUntilDone(75000)
  if (st && st.hitBaud > 0 && st.hitProtocol) {
    // ③ 用诊断命中参数重连 + 数据门禁
    onStage('diagnose-hit', { port, baudRate: st.hitBaud, protocol: st.hitProtocol })
    const startedAt = Date.now()
    await ScaleClient.openScale({ scaleKey: key, port, baudRate: st.hitBaud, protocol: st.hitProtocol })
    if (await ScaleClient.waitForReading(key, 5000, startedAt)) {
      onStage('done', { ok: true, via: 'diagnose' })
      return { ok: true, via: 'diagnose', port, baudRate: st.hitBaud, protocol: st.hitProtocol }
    }
    try { await ScaleClient.closeScale(key) } catch (e) { /* 忽略 */ }
    onStage('done', { ok: false })
    return {
      ok: false, port,
      failStep: 'detect',
      failSummary: `诊断识别到 ${st.hitProtocolLabel || st.hitProtocol} @${st.hitBaud}，但连接后收不到读数（已自动断开）`,
      failSolution: '请检查串口线是否接触不良；或稍等几秒后重试。'
    }
  }

  // ④ 失败：取诊断第一个 fail 步骤作为根因
  const failStep = (st && st.steps && st.steps.find(s => s.status === 'fail')) || null
  onStage('done', { ok: false })
  return {
    ok: false, port,
    failStep: failStep ? failStep.key : '',
    failSummary: (failStep && failStep.summary) || (st && st.error) || '端口检测未通过',
    failSolution: (failStep && failStep.solution) || ''
  }
}

// pollDiagnoseUntilDone 轮询统一诊断直到结束（500ms 一次），超时按当前快照返回。
async function pollDiagnoseUntilDone(timeoutMS) {
  const deadline = Date.now() + (timeoutMS || 75000)
  let last = null
  while (Date.now() < deadline) {
    try {
      last = await ScaleClient.getDiagnoseStatus()
      if (last && !last.running) return last
    } catch (e) { /* 单次失败继续 */ }
    await new Promise((r) => setTimeout(r, 500))
  }
  return last
}

export { ScaleClient as default, ScaleStream, smartConnectScale }
